import React, { useEffect, useState } from 'react';
import { FiUsers, FiLink, FiShield, FiCheckCircle, FiDollarSign, FiRefreshCw, FiFolder, FiClock, FiXCircle, FiArrowRight } from 'react-icons/fi';
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, PieChart, Pie, Cell, Legend } from 'recharts';
import { format } from 'date-fns';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/layout/Layout';
import Card from '../components/common/Card';
import Button from '../components/common/Button';
import Badge from '../components/common/Badge';
import dashboardService from '../services/dashboardService';
import { toast } from 'react-toastify';
import './Dashboard.css';

const STATUS_COLORS = {
  pending: '#f59e0b',
  approved: '#3b82f6',
  processing: '#8b5cf6',
  completed: '#10b981',
  rejected: '#ef4444',
  failed: '#dc2626'
};

const Dashboard = () => {
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [chartData, setChartData] = useState([]);
  const [loading, setLoading] = useState(true);

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [statsResponse, chartResponse] = await Promise.all([
        dashboardService.getStats(),
        dashboardService.getChartData()
      ]);
      setStats(statsResponse.data);
      setChartData(chartResponse.data);
    } catch (error) {
      toast.error('Failed to load dashboard data');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const statCards = [
    {
      title: 'Pending Approval',
      icon: <FiClock />,
      value: stats?.pendingCount || 0,
      color: 'warning'
    },
    {
      title: 'Approved',
      icon: <FiCheckCircle />,
      value: stats?.approvedCount || 0,
      color: 'success'
    },
    {
      title: 'Rejected',
      icon: <FiXCircle />,
      value: stats?.rejectedCount || 0,
      color: 'danger'
    },
    {
      title: 'Total Transactions',
      icon: <FiDollarSign />,
      value: stats?.transactions || 0,
      color: 'info'
    },
    {
      title: 'Active Projects',
      icon: <FiFolder />,
      value: stats?.projects || 0,
      color: 'primary'
    }
  ];

  const systemCards = [
    { title: 'Team Members', icon: <FiUsers />, value: stats?.teamMembers || 0, link: '/users' },
    { title: 'Webhooks', icon: <FiLink />, value: stats?.webhooks || 0, link: '/webhooks' },
    { title: 'Whitelisted IPs', icon: <FiShield />, value: stats?.whitelistedIps || 0, link: '/ip-whitelist' }
  ];

  const getStatusVariant = (status) => {
    switch (status) {
      case 'pending': return 'warning';
      case 'approved': case 'processing': return 'info';
      case 'completed': return 'success';
      case 'failed': case 'rejected': return 'danger';
      default: return 'default';
    }
  };

  const pieChartData = stats?.statusBreakdown?.map(item => ({
    name: item.status.charAt(0).toUpperCase() + item.status.slice(1),
    value: item.count,
    color: STATUS_COLORS[item.status] || '#6b7280'
  })) || [];

  const CustomTooltip = ({ active, payload, label }) => {
    if (active && payload && payload.length) {
      return (
        <div className="chart-tooltip">
          <p className="chart-tooltip-label">{label}</p>
          {payload.map((entry, index) => (
            <p key={index} className="chart-tooltip-value" style={{ color: entry.color }}>
              <span>{entry.name}:</span> {entry.value}
            </p>
          ))}
        </div>
      );
    }
    return null;
  };

  const renderCustomizedLabel = ({ cx, cy, midAngle, innerRadius, outerRadius, percent }) => {
    if (percent < 0.05) return null;
    const RADIAN = Math.PI / 180;
    const radius = innerRadius + (outerRadius - innerRadius) * 0.5;
    const x = cx + radius * Math.cos(-midAngle * RADIAN);
    const y = cy + radius * Math.sin(-midAngle * RADIAN);
    return (
      <text x={x} y={y} fill="white" textAnchor="middle" dominantBaseline="central" fontSize={12} fontWeight={600}>
        {`${(percent * 100).toFixed(0)}%`}
      </text>
    );
  };

  return (
    <Layout title="Dashboard">
      <div className="dashboard">
        <div className="dashboard-header">
          <div>
            <h2>Dashboard</h2>
            <p>Overview of your Herald Processing Unit</p>
          </div>
          <Button onClick={fetchDashboardData} loading={loading}>
            <FiRefreshCw />
            Refresh
          </Button>
        </div>

        {/* Transaction Stats */}
        <div className="stats-grid">
          {statCards.map((stat) => (
            <Card key={stat.title} className={`stat-card stat-${stat.color}`}>
              <div className="stat-content">
                <div className="stat-header">
                  <span className={`stat-icon stat-icon-${stat.color}`}>
                    {stat.icon}
                  </span>
                  <span className="stat-title">{stat.title}</span>
                </div>
                <div className="stat-value">
                  {stat.value}
                </div>
              </div>
            </Card>
          ))}
        </div>

        {/* Charts Row */}
        <div className="charts-row">
          {/* Transaction Trend Chart */}
          <div className="chart-card-wide">
            <Card title="Transaction Trend - Last 10 Days">
              <div className="chart-container">
                {chartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <AreaChart data={chartData} margin={{ top: 10, right: 30, left: 0, bottom: 0 }}>
                      <defs>
                        <linearGradient id="colorPending" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={STATUS_COLORS.pending} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={STATUS_COLORS.pending} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorApproved" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={STATUS_COLORS.approved} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={STATUS_COLORS.approved} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorRejected" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={STATUS_COLORS.rejected} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={STATUS_COLORS.rejected} stopOpacity={0} />
                        </linearGradient>
                        <linearGradient id="colorCompleted" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="5%" stopColor={STATUS_COLORS.completed} stopOpacity={0.3} />
                          <stop offset="95%" stopColor={STATUS_COLORS.completed} stopOpacity={0} />
                        </linearGradient>
                      </defs>
                      <CartesianGrid strokeDasharray="3 3" stroke="var(--color-border)" />
                      <XAxis
                        dataKey="label"
                        stroke="var(--color-text-secondary)"
                        fontSize={12}
                        tickLine={false}
                      />
                      <YAxis
                        stroke="var(--color-text-secondary)"
                        fontSize={12}
                        tickLine={false}
                        axisLine={false}
                        allowDecimals={false}
                      />
                      <Tooltip content={<CustomTooltip />} />
                      <Legend
                        verticalAlign="top"
                        height={36}
                        formatter={(value) => <span style={{ color: 'var(--text-secondary)', fontSize: '12px', textTransform: 'capitalize' }}>{value}</span>}
                      />
                      <Area
                        type="monotone"
                        dataKey="pending"
                        name="Pending"
                        stroke={STATUS_COLORS.pending}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorPending)"
                      />
                      <Area
                        type="monotone"
                        dataKey="approved"
                        name="Approved"
                        stroke={STATUS_COLORS.approved}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorApproved)"
                      />
                      <Area
                        type="monotone"
                        dataKey="rejected"
                        name="Rejected"
                        stroke={STATUS_COLORS.rejected}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorRejected)"
                      />
                      <Area
                        type="monotone"
                        dataKey="completed"
                        name="Completed"
                        stroke={STATUS_COLORS.completed}
                        strokeWidth={2}
                        fillOpacity={1}
                        fill="url(#colorCompleted)"
                      />
                    </AreaChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-empty">
                    <p>No transaction data available for the last 10 days</p>
                  </div>
                )}
              </div>
            </Card>
          </div>

          {/* Status Breakdown Pie Chart */}
          <div className="chart-card-small">
            <Card title="Status Breakdown">
              <div className="chart-container">
                {pieChartData.length > 0 ? (
                  <ResponsiveContainer width="100%" height={280}>
                    <PieChart>
                      <Pie
                        data={pieChartData}
                        cx="50%"
                        cy="50%"
                        labelLine={false}
                        label={renderCustomizedLabel}
                        outerRadius={100}
                        fill="#8884d8"
                        dataKey="value"
                      >
                        {pieChartData.map((entry, index) => (
                          <Cell key={`cell-${index}`} fill={entry.color} />
                        ))}
                      </Pie>
                      <Legend
                        verticalAlign="bottom"
                        iconType="circle"
                        iconSize={8}
                        formatter={(value) => <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{value}</span>}
                      />
                    </PieChart>
                  </ResponsiveContainer>
                ) : (
                  <div className="chart-empty">
                    <p>No data available</p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>

        {/* Bottom Row */}
        <div className="bottom-row">
          {/* Recent Transactions */}
          <div className="recent-transactions-card">
            <Card
              title="Recent Transactions"
              actions={
                <Button variant="secondary" size="small" onClick={() => navigate('/transactions')}>
                  View All <FiArrowRight />
                </Button>
              }
            >
              <div className="recent-transactions">
                {stats?.recentTransactions?.length > 0 ? (
                  <table className="recent-table">
                    <thead>
                      <tr>
                        <th>Reference</th>
                        <th>Project</th>
                        <th>Amount</th>
                        <th>Status</th>
                        <th>Date</th>
                      </tr>
                    </thead>
                    <tbody>
                      {stats.recentTransactions.map((tx) => (
                        <tr key={tx.id}>
                          <td className="mono">{tx.reference_id}</td>
                          <td>{tx.project?.display_name || '-'}</td>
                          <td className="amount">
                            {parseFloat(tx.amount).toLocaleString('en-US', { minimumFractionDigits: 2 })} {tx.currency}
                          </td>
                          <td>
                            <Badge variant={getStatusVariant(tx.status)}>{tx.status}</Badge>
                          </td>
                          <td>{format(new Date(tx.created_at), 'MMM dd, HH:mm')}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                ) : (
                  <div className="no-data">No recent transactions</div>
                )}
              </div>
            </Card>
          </div>

          {/* System Stats */}
          <div className="system-stats-card">
            <Card title="System Overview">
              <div className="system-stats">
                {systemCards.map((item) => (
                  <div
                    key={item.title}
                    className="system-stat-item"
                    onClick={() => navigate(item.link)}
                  >
                    <div className="system-stat-icon">{item.icon}</div>
                    <div className="system-stat-info">
                      <span className="system-stat-value">{item.value}</span>
                      <span className="system-stat-title">{item.title}</span>
                    </div>
                    <FiArrowRight className="system-stat-arrow" />
                  </div>
                ))}
              </div>
            </Card>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default Dashboard;
